// src/WebServer/OncoRisk/content/modules/analysistools/multi-variant-search.js

// Keep existing imports
import React, { useState, useCallback } from 'react';
import { getApiEndpoint } from '../../../config/oncorisk-config';
import DetailView from '../detail-view';
import './multi-variant-search.css';
import OncoRiskNetworkGraph from './OncoRiskNetworkGraph';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCsv, faPlus } from '@fortawesome/free-solid-svg-icons';

function MultiVariantSearch() {
  const [input, setInput] = useState('');
  const [terms, setTerms] = useState([]);
  const [rawResultsByTerm, setRawResultsByTerm] = useState([]);
  const [displayableResults, setDisplayableResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);

  // This state will hold the fully-detailed data needed for the network graph
  const [enrichedResults, setEnrichedResults] = useState([]);

  const addTerm = useCallback(() => {
    const t = input.trim();
    if (!t || terms.includes(t)) {
      setInput('');
      return;
    }
    setTerms((prev) => [...prev, t]);
    setInput('');
  }, [input, terms]);

  const removeTerm = useCallback((idxToRemove) => {
    setTerms((prev) => {
      const newTerms = prev.filter((_, i) => i !== idxToRemove);
      if (newTerms.length === 0) {
        setSearchAttempted(false);
        setRawResultsByTerm([]);
        setDisplayableResults([]);
        setEnrichedResults([]); // Also clear enriched data
      }
      return newTerms;
    });
  }, []);

  const handleSearch = useCallback(async () => {
    setSearchAttempted(true);
    setRawResultsByTerm([]);
    setDisplayableResults([]);
    setEnrichedResults([]); // Reset on new search

    if (terms.length === 0) {
      setSearchAttempted(false);
      return;
    }

    setLoading(true);

    // --- STAGE 1: Get initial search results for all terms ---
    const initialSearchResults = [];
    // These are the types we consider "central nodes" for our graph
    const mainNodeTypes = ['features', 'molecularprofiles'];

    for (const t of terms) {
      try {
        const resp = await fetch(`${getApiEndpoint('search')}?term=${encodeURIComponent(t)}`);
        if (!resp.ok) throw new Error(`API Error ${resp.status}`);
        const data = await resp.json();
        
        // ** THIS IS THE KEY CHANGE **
        // Instead of only accepting 'features', we now accept any of our main node types.
        const filtered = (Array.isArray(data) ? data : []).filter(item => 
            item && mainNodeTypes.includes((item.itemType || '').toLowerCase())
        );

        initialSearchResults.push({ term: t, items: filtered });
      } catch (e) {
        console.error(`Error searching for term "${t}":`, e);
        initialSearchResults.push({ term: t, items: [], error: e.message });
      }
    }
    setRawResultsByTerm(initialSearchResults);

    // --- STAGE 2: De-duplicate results and prepare for enrichment ---
    const uniqueItemsMap = new Map();
    initialSearchResults.forEach(res => {
        res.items.forEach(item => {
            if (item && item.id) {
                // Store the item with its original search term for context
                uniqueItemsMap.set(item.id, { ...item, originalTerm: res.term });
            }
        });
    });
    const itemsToEnrich = Array.from(uniqueItemsMap.values());
    setDisplayableResults(itemsToEnrich);

    // --- STAGE 3: Fetch full details for each unique item to build the network ---
    if (itemsToEnrich.length > 0) {
      const detailEndpointBase = `https://service8.szapfs.org/api/oncorisk`;
      const detailPromises = itemsToEnrich.map(item =>
        fetch(`${detailEndpointBase}/${item.itemType}/${item.id}`)
          .then(res => {
            if (!res.ok) return Promise.reject(`Failed to fetch details for ${item.name}`);
            return res.json();
          })
      );

      try {
        const allDetails = (await Promise.allSettled(detailPromises))
        .filter(result => result.status === 'fulfilled')
        .map((result, index) => ({
          ...result.value,
          // CRITICAL: Add the 'type' property from the original item
          type: itemsToEnrich[index].itemType
        }));
              setEnrichedResults(allDetails);
      } catch (error) {
        console.error("Error fetching detailed data for network graph:", error);
      }
    }

    setLoading(false);
  }, [terms]);

  const openModalWithItem = useCallback((item) => {
    const modalItem = {
      // The item from the graph node might have a different structure
      id: item.id,
      name: item.name,
      // Ensure we get the type correctly from either a search result or a graph node
      itemType: item.type || item.itemType || 'features'
    };
    setSelectedItemForModal(modalItem);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedItemForModal(null);
  }, []);

  const handleNavigationInModal = useCallback((type, id, name) => {
    setSelectedItemForModal({
      itemType: type,
      id: id,
      name: name
    });
  }, []);

  const getTermErrors = () => rawResultsByTerm.filter(r => r.error).map(r => ({term: r.term, error: r.error}));
  
  // The JSX below does not require any changes. It is already generic enough.
  return (
    <div className="OCRP-mvs-page">
      <div className="OCRP-mvs-page-left">
        <div className="OCRP-mvs-page-left-top">
          <div className="OCRP-mvs-add">


            <div className="OCRP-mvs-add-btn ">

              <FontAwesomeIcon icon={faFileCsv} />
              
            </div>


            <input
              className="OCRP-text-input"
              type="text"
              value={input}
              placeholder="Add feature or profile... (Ex. BRCA1, TP53)"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTerm();
                }
              }}
            />
            <button
              className="OCRP-button-add OCRP-mvs-add-btn"
              onClick={(e) => {
                e.preventDefault();
                addTerm();
              }}
              aria-label="Add term"
              disabled={!input.trim()}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>
        <div className="OCRP-mvs-page-left-middle">
          {terms.length > 0 && (
            <div className="OCRP-mvs-term-list">
              {terms.map((t, idx) => (
                <div key={idx} className="OCRP-mvs-term">
                  <span>{t.toUpperCase()}</span>
                  <button
                    className="OCRP-button-add OCRP-mvs-remove-btn"
                    onClick={() => removeTerm(idx)}
                    aria-label={`Remove term ${t}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="OCRP-mvs-page-left-bottom">
          <button
            className="OCRP-mvs-search-btn"
            onClick={handleSearch}
            disabled={loading || terms.length === 0}
          >
            {loading ? (
                <i className="fas fa-spinner fa-spin"></i>
            ) : (
                <i className="fas fa-search"></i>
            )}
            <span>{loading ? 'Searching...' : 'Search'}</span>
          </button>
        </div>
      </div>
      
      <div className="OCRP-mvs-page-right">
        <div className="OCRP-mvs-page-right-results">
          {loading && !searchAttempted && <div className="OCRP-mvs-loading">Searching...</div>}
          {!loading && searchAttempted && displayableResults.length === 0 && getTermErrors().length === 0 && (
            <p className="OCRP-mvs-empty">No Results</p>
          )}
          {!loading && getTermErrors().length > 0 && (
            <div className="OCRP-mvs-error-summary">
              <h4>Search Errors:</h4>
              {getTermErrors().map(err => (
                <p key={err.term} className="OCRP-mvs-error">Error for "{err.term}": {err.error}</p>
              ))}
            </div>
          )}
          {!loading && displayableResults.length > 0 && (
            <div className="OCRP-mvs-result-tile-grid">
              {displayableResults.map((item) => (
                <div 
                  key={item.id} 
                  className="OCRP-mvs-result-tile" 
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openModalWithItem(item); }}
                >

                  <span className="OCRP-mvs-tile-dot"></span>
                  <div className="OCRP-mvs-tile-content" onClick={() => openModalWithItem(item)}>
                    <span className="OCRP-mvs-tile-name">
                      
                     <div>{item.name || item.id}</div>
                     <div className="OCRP-mvs-tile-type">{item.originalTerm ? `(${item.originalTerm})` : (item.itemType ? `(${item.itemType})` : null)}</div>
                      
                      
                      </span>
                  </div>
                  <button 
                    className="OCRP-mvs-tile-remove" 
                    onClick={(e) => {
                      e.stopPropagation();
                      // Find matching term and remove it
                      const termIndex = terms.findIndex(term => 
                        displayableResults.find(res => res.id === item.id && res.name.includes(term)));
                      if (termIndex !== -1) {
                        removeTerm(termIndex);
                      }
                    }}
                    aria-label={`Remove ${item.name}`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="OCRP-mvs-page-right-network">
          <OncoRiskNetworkGraph 
            data={enrichedResults} 
            onNodeClick={openModalWithItem} 
          />
        </div>
      </div>
      
      {isModalOpen && selectedItemForModal && (
        <div className="OCRP-mvs-modal-overlay" onClick={closeModal}>
          <div className="OCRP-mvs-modal-content" onClick={(e) => e.stopPropagation()}>
            <DetailView 
              itemType={selectedItemForModal.itemType}
              itemId={selectedItemForModal.id}
              itemName={selectedItemForModal.name} 
              onBack={closeModal} 
              onNavigate={handleNavigationInModal}
            /> 
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiVariantSearch;